/**
 * Auto-title policy (pure — no I/O, no React).
 *
 * Two decisions live here: whether to fire the titler at all, and whether a
 * non-fatal skip should be retried.
 *
 * The auto-title client trigger POSTs to /api/sessions/[id]/auto-title. The
 * route returns `200 {skipped: <reason>}` for non-fatal outcomes, so the
 * caller's `.then` runs (not `.catch`). Without a retry policy a transient skip
 * left the session's marker set and it never retried — the root cause of
 * default titles surviving many turns.
 */

/**
 * How many times one conversation may be auto-titled.
 *
 * Titling once and never again leaves a drifted conversation mislabelled: a
 * session titled from a question about ablation safety endpoints, then asked
 * about CDASH Vital Signs and ICD-10 codes, still read as the former after
 * three turns (measured on the rig). ChatGPT behaves this way and its users ask
 * for dynamic updates because "conversations evolve significantly from the
 * initial question".
 *
 * But unbounded retitling is worse. Claude Code renames during the session and
 * users asked for an off switch because "the title gets overwritten multiple
 * times during a conversation, losing the user's original naming convention".
 * A sidebar label that keeps moving is harder to navigate than a slightly stale
 * one, so the cap is deliberately low.
 */
export const MAX_AUTO_TITLES = 3

/**
 * Messages of drift before a retitle is allowed.
 *
 * Measured in messages rather than turns because that is what the index already
 * tracks. Six is roughly three exchanges — long enough that the subject really
 * has moved, rather than the model having elaborated on the same one.
 */
export const RETITLE_AFTER_MESSAGES = 6

export interface AutoTitleState {
  /** Message count at the moment we last titled this session. */
  titledAtMessageCount?: number
  /** How many times this session has been auto-titled. */
  titleCount: number
  /** True once the user has renamed it by hand. */
  titleLocked: boolean
}

/**
 * Should the titler run for this conversation right now?
 *
 * The lock check is first and unconditional: a hand-edited title stays static
 * from that point on, whatever the conversation goes on to discuss. The server
 * enforces the same rule through title precedence — a `generated` write cannot
 * replace a `user` one — so this is the cheap half of a belt-and-braces pair.
 * Checking here avoids paying for a model call whose result would be refused.
 */
export function shouldAutoTitle(messageCount: number, state: AutoTitleState): boolean {
  if (state.titleLocked) return false
  if (state.titleCount === 0) return true
  if (state.titleCount >= MAX_AUTO_TITLES) return false

  const drift = messageCount - (state.titledAtMessageCount ?? 0)
  return drift >= RETITLE_AFTER_MESSAGES
}

/**
 * Permanent (do NOT retry): `user-set-title` — already titled/locked; retrying
 * is wasteful and could race a manual rename. Everything else, including
 * unknown future reasons, is transient → retry on the next assistant turn so a
 * conversation eventually gets a title.
 */
// 'title-not-accepted' means the server refused the write on precedence —
// almost always a manual rename outranking us. Regenerating would be refused
// again, so it is permanent for this session.
const PERMANENT_SKIP_REASONS = new Set<string>(['user-set-title', 'title-not-accepted'])

export function shouldRetryAutoTitle(skipReason: string): boolean {
  return !PERMANENT_SKIP_REASONS.has(skipReason)
}

/** What the caller should do with an auto-title response. */
export type AutoTitleOutcome =
  | { kind: 'titled'; title: string }
  | { kind: 'retry'; reason: string }
  | { kind: 'stop'; reason: string; title?: string }

/**
 * Read the route's response, which answers 200 for every outcome.
 *
 * A SKIP WINS OVER AN ACCOMPANYING TITLE, and that ordering is the whole point
 * of this function. Several skip branches also carry a `title`:
 * `register-failure` returns the title it generated, `title-not-accepted`
 * returns the incumbent. A caller that checks `data.title` first reads those as
 * successes — observed live, where a retitle refused by the server on precedence
 * logged as `new title: CRYO-1 My Own Label`, the user's own existing label. The
 * title was never at risk (the server refuses the write) but the caller did not
 * learn to stop, so it would burn its remaining retitle budget on writes certain
 * to fail.
 *
 * Anything unreadable retries: a missed title costs one more attempt next turn,
 * while a wrongly-permanent stop leaves a conversation mislabelled for good.
 */
export function interpretAutoTitleResponse(data: unknown): AutoTitleOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as
    { title?: unknown; skipped?: unknown }

  const skipped = typeof d.skipped === 'string' ? d.skipped : null
  if (skipped) {
    const title = typeof d.title === 'string' && d.title ? d.title : undefined
    return shouldRetryAutoTitle(skipped)
      ? { kind: 'retry', reason: skipped }
      : { kind: 'stop', reason: skipped, ...(title ? { title } : {}) }
  }

  if (typeof d.title === 'string' && d.title) return { kind: 'titled', title: d.title }
  return { kind: 'retry', reason: 'no-title-no-skip' }
}
